import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import SimpleBar from 'simplebar-react'

import Loader from 'Components/Common/Loader'
import {
    createCreatorTag,
    deleteCreatorTag,
    fetchCreatorTags,
    updateCreatorTag,
} from 'services/api/creator-tag'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { resetCreatorsState } from 'slices/creators/reducer'
import { AppDispatch, RootState } from 'store'
import { CreatorTag } from 'types/creators'
import { getErrorMessage } from 'utils/helpers'
import inOffcanvas from 'utils/hoc/inOffcanvas'
import './ManageCreatorTagsOffcanvas.scss'

interface ManageCreatorTagsOffcanvasProps {
    onClose: () => void
}

const normalizeTagName = (name: string) => name.trim()

/** API may omit uuid; numeric id is always unique for list/edit state. */

const ManageCreatorTagsOffcanvasContent: React.FC<ManageCreatorTagsOffcanvasProps> = ({
    onClose,
}) => {
    const dispatch = useDispatch<AppDispatch>()
    const hubId = useSelector((state: RootState) => state.auth.selectedHubId)
    const filters = useSelector((state: RootState) => state.creators.filters)

    const [tags, setTags] = useState<CreatorTag[]>([])
    const [loading, setLoading] = useState(false)
    const [newTagName, setNewTagName] = useState('')
    const [creating, setCreating] = useState(false)
    const [editingTagId, setEditingTagId] = useState<number | null>(null)
    const [editingName, setEditingName] = useState('')
    const [savingTagId, setSavingTagId] = useState<number | null>(null)
    const [deletingTagId, setDeletingTagId] = useState<number | null>(null)

    const refreshCreatorsList = useCallback(() => {
        dispatch(resetCreatorsState())
        dispatch(fetchContactsThunk({ force: true, filters: { ...filters, page: 1 } }))
    }, [dispatch, filters])

    const loadTags = useCallback(async () => {
        if (!hubId) return
        setLoading(true)
        try {
            const data = await fetchCreatorTags(hubId)
            setTags(data)
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to load tags.'))
        } finally {
            setLoading(false)
        }
    }, [hubId])

    useEffect(() => {
        loadTags()
    }, [loadTags])

    const isDuplicateName = (name: string, excludeTagId?: number) => {
        const normalized = normalizeTagName(name).toLowerCase()
        return tags.some(
            (tag) =>
                tag.id !== excludeTagId &&
                normalizeTagName(tag.tag).toLowerCase() === normalized
        )
    }

    const handleCreate = async () => {
        const name = normalizeTagName(newTagName)
        if (!hubId || !name) return
        if (isDuplicateName(name)) {
            toast.error('A tag with this name already exists.')
            return
        }
        try {
            setCreating(true)
            const created = await createCreatorTag(hubId, name)
            loadTags()
            setNewTagName('')
            refreshCreatorsList()
            toast.success('Tag created successfully.')
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to create tag.'))
        } finally {
            setCreating(false)
        }
    }

    const startEditing = (tag: CreatorTag) => {
        setEditingTagId(tag.id)
        setEditingName(tag.tag)
    }

    const cancelEditing = () => {
        setEditingTagId(null)
        setEditingName('')
    }

    const handleUpdate = async (tag: CreatorTag) => {
        const name = normalizeTagName(editingName)
        if (!hubId || !name) return
        if (name === tag.tag) {
            cancelEditing()
            return
        }
        if (isDuplicateName(name, tag.id)) {
            toast.error('A tag with this name already exists.')
            return
        }
        try {
            setSavingTagId(tag.id)
            const updated = await updateCreatorTag(hubId, tag.id, name)
            loadTags()
            cancelEditing()
            refreshCreatorsList()
            toast.success('Tag updated successfully.')
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to update tag.'))
        } finally {
            setSavingTagId(null)
        }
    }

    const handleDelete = async (tag: CreatorTag) => {
        if (!hubId) return

        const result = await Swal.fire({
            title: `Delete "${tag.tag}"?`,
            text: 'Creators using this tag will no longer have it assigned.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#fa896b',
        })

        if (!result.isConfirmed) return

        try {
            setDeletingTagId(tag.id)
            await deleteCreatorTag(hubId, tag.id)
            setTags((prev) => prev.filter((item) => item.id !== tag.id))
            if (editingTagId === tag.id) cancelEditing()
            refreshCreatorsList()
            toast.success('Tag deleted successfully.')
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to delete tag.'))
        } finally {
            setDeletingTagId(null)
        }
    }

    return (
        <div className="manage-creator-tags-offcanvas">
            <div className="manage-creator-tags-offcanvas__create mb-4">
                <label htmlFor="new-creator-tag" className="form-label">
                    New tag
                </label>
                <div className="d-flex gap-2">
                    <input
                        id="new-creator-tag"
                        type="text"
                        className="form-control"
                        placeholder="Enter tag name"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCreate()
                        }}
                        disabled={creating || !hubId}
                    />
                    <button
                        type="button"
                        className="theme-btn btn-primary flex-shrink-0"
                        onClick={handleCreate}
                        disabled={creating || !normalizeTagName(newTagName) || !hubId}
                    >
                        {creating ? 'Adding...' : 'Add'}
                    </button>
                </div>
            </div>

            <SimpleBar className="manage-creator-tags-offcanvas__scroll" style={{ height: '100%' }}>
                {loading ? (
                    <div className="manage-creator-tags-offcanvas__loader py-4">
                        <Loader />
                    </div>
                ) : tags.length === 0 ? (
                    <div className="text-muted text-center py-4">No tags yet. Add your first tag above.</div>
                ) : (
                    <ul className="manage-creator-tags-offcanvas__list list-unstyled mb-0">
                        {tags.map((tag) => {
                            const isEditing = editingTagId === tag.id
                            const isSaving = savingTagId === tag.id
                            const isDeleting = deletingTagId === tag.id

                            return (
                                <li key={tag.id} className="manage-creator-tags-offcanvas__item">
                                    {isEditing ? (
                                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleUpdate(tag)
                                                    if (e.key === 'Escape') cancelEditing()
                                                }}
                                                autoFocus
                                                disabled={isSaving}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-soft-success"
                                                onClick={() => handleUpdate(tag)}
                                                disabled={
                                                    isSaving ||
                                                    !normalizeTagName(editingName) ||
                                                    normalizeTagName(editingName) === tag.tag
                                                }
                                                aria-label="Save tag"
                                            >
                                                <i className="ri-check-line" />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-soft-secondary"
                                                onClick={cancelEditing}
                                                disabled={isSaving}
                                                aria-label="Cancel edit"
                                            >
                                                <i className="ri-close-line" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="manage-creator-tags-offcanvas__name">{tag.tag}</span>
                                            <div className="d-flex align-items-center gap-1">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-ghost-secondary"
                                                    onClick={() => startEditing(tag)}
                                                    disabled={isDeleting || !!editingTagId}
                                                    aria-label={`Edit ${tag.tag}`}
                                                >
                                                    <i className="ri-pencil-line" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-ghost-danger"
                                                    onClick={() => handleDelete(tag)}
                                                    disabled={isDeleting || !!editingTagId}
                                                    aria-label={`Delete ${tag.tag}`}
                                                >
                                                    <i className="ri-delete-bin-line" />
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </li>
                            )
                        })}
                    </ul>
                )}
            </SimpleBar>
            <div className="manage-creator-tags-offcanvas__footer">
                <button type="button" className="theme-btn btn-primary-outlined w-100" onClick={onClose}>
                    Done
                </button>
            </div>
        </div>
    )
}

export default inOffcanvas(ManageCreatorTagsOffcanvasContent, 'Manage Tags', 'Create, rename, or remove tags used to organize your creators.')
